import React, { useEffect, useState } from "react";
import { Navbar, Container, Offcanvas } from "react-bootstrap";
import { CloseButton, HamburgerButton, MyAccountIcon, ShoppingBagIcon } from "../components";
import { useScrollToTopAndNavigate } from "../hooks";
import { useSelector } from "react-redux";
import { selectCartItemCount } from "../redux/slices/cartSlice";

interface NavigationProps {
  headerHeight: number;
  logoUrl: string;
  logoOffcanvasUrl: string;
  hiddenRoutes: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ headerHeight, logoUrl, logoOffcanvasUrl, hiddenRoutes }) => {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [activeNav, setActiveNav] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [showLogo, setShowLogo] = useState(false);
  const [isOverHeader, setIsOverHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const itemCount = useSelector(selectCartItemCount);

  const handleNavigation = useScrollToTopAndNavigate()

  const navigationData = [
    {
      label: "About",
      path: "/about",
      subItems: [],
    },
    {
      label: "FAQs",
      path: "/faqs",
      subItems: [],
    },
    {
      label: "Shop",
      path: "/shop",
      subItems: [
        { label: "Brote", path: "/shop?category=bread" },
        { label: "Kuchen", path: "/shop?category=cakes" },
        { label: "Gebäck", path: "/shop?category=pastry" },
      ],
    },
    {
      label: "Contact",
      path: "/contact",
      subItems: [],
    },
    {
      label: "Business",
      path: "/b2b#section_startprint",
      subItems: [],
    }
  ];
  
  useEffect(() => {
    if (hiddenRoutes) {
      setShowLogo(true);
      setIsOverHeader(false);
    }
  }, [hiddenRoutes]);
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > headerHeight) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      if (hiddenRoutes) {
        setShowLogo(true);
        setIsOverHeader(false);
      } else { 
        setIsOverHeader(currentScrollY < headerHeight);
        setShowLogo(currentScrollY > headerHeight);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY, headerHeight]);

  const navbarStyle: React.CSSProperties = {
    position: "fixed",
    top: isVisible ? 0 : "-200px",
    width: "100%",
    transition: "top 0.3s ease-in-out, background-color 0.3s ease-in-out",
    background: isOverHeader
      ? "linear-gradient(#000, #000 48%, #0000)"
      : "white",
    boxShadow: isOverHeader ? "none" : "0px 2px 5px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
  };

  const linkStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Regular",
    fontSize: "2.4rem",
    animation: "fadeIn 2s ease-in-out forwards",
  }

  const logoStyle: React.CSSProperties = {
    width: "200px",
    maxWidth: "18vh",
    height: "auto",
    opacity: 1,
    animation: "fadeIn 2s ease-in-out forwards",
  };

  const iconStyle: React.CSSProperties = {
    marginLeft: "20px",
    cursor: "pointer",
  };

  const textContainerStyle: React.CSSProperties = {
    position: "absolute",
    bottom: "0",
    right: "0",
    transform: "translate(-10%, -25%) rotate(-17deg)",
    textAlign: "center",
    zIndex: 1,
    fontSize: "6vmax",
    fontFamily: "Alfa Slab One",
    fontWeight: 300,
    opacity: 0,
    animation: "fadeInText 1s ease-in-out 0.5s forwards",
  };

  const glutenFreeStyle: React.CSSProperties = {
    color: "#A67C52",
    marginTop: "-1.8rem",
  };

  const handleHover = (label: string) => {
    setActiveNav(label);
  };

  const navigateAndClose = (path: string) => {
    handleNavigation(path);
    setShowOffcanvas(false);
  };

  return (
    <Navbar style={navbarStyle} collapseOnSelect expand="md">
      <Container style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

        <div style={{ flex: "1", display: "flex", justifyContent: "flex-start" }}>
          <HamburgerButton onClick={() => setShowOffcanvas(true)} />
        </div>
        
        <div style={{ flex: "1", display: "flex", justifyContent: "center" }}>
          <Navbar.Brand onClick={() => handleNavigation("/")}>
            <img src={showLogo ? logoUrl : logoOffcanvasUrl} alt="Logo" style={logoStyle} />
          </Navbar.Brand>
        </div>
        
        <div style={{ flex: "1", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <div style={iconStyle}>
            <MyAccountIcon onClick={() => handleNavigation("/login#section_start")} />
          </div>
          <div style={iconStyle}>
            <ShoppingBagIcon onClick={() => handleNavigation("/checkout")} itemCount={itemCount ?? 0}/>
          </div>
        </div>

        <Offcanvas
          show={showOffcanvas}
          id={`offcanvasNavbar-expand-lg`}
          aria-labelledby={`offcanvasNavbarLabel-expand-lg`}
          placement="end"
          style={{
            width: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Offcanvas.Header>
            <Container style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: "1", display: "flex", justifyContent: "flex-start" }}>
                <CloseButton onClick={()=>setShowOffcanvas(false)} />
              </div>
              <div style={{ flex: "1", display: "flex", justifyContent: "center" }}>
                <img src={logoOffcanvasUrl} alt="Logo" style={{...logoStyle, animation: ''}} />
              </div>
            </Container>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Container>
              <div style={{ display: "flex", height: "100%", flexDirection: "row" }}>

                <div style={{ flex: 1, padding: "20px 15px" }}>
                  {navigationData.map((navItem, index) => {
                    const isActive = navItem.label === activeNav;
                    return (
                      <div
                        key={index}
                        onMouseEnter={() => handleHover(navItem.label)}
                        onClick={() => navigateAndClose(navItem.path)}
                        style={{
                          fontSize: "1.8rem",
                          color: isActive ? "#c39d63" : "#ffffffe6",
                          cursor: "pointer",
                          marginBottom: "18px",
                          fontFamily: "Avenir Next Codensed Regular",
                          borderBottom: isActive ? "2px solid #c39d63" : "2px solid transparent",
                          paddingBottom: "6px",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {navItem.label}
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    width: "1px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    margin: "0 10px",
                    transition: "opacity 0.3s",
                  }}
                />

                <div style={{ flex: 1, padding: "20px 15px" }}>
                  {navigationData
                    .find((item) => item.label === activeNav)
                    ?.subItems.map((subItem, subIndex) => (
                      <div
                        key={subIndex}
                        onClick={() => navigateAndClose(subItem.path)}
                        style={{
                          fontSize: "1.5rem",
                          color: "#ffffffe6",
                          marginBottom: "14px",
                          cursor: "pointer",
                          fontFamily: "Avenir Next Codensed Regular",
                          paddingLeft: "5px",
                          borderLeft: "3px solid transparent",
                          transition: "color 0.3s, border 0.3s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderLeft = "3px solid #c39d63";
                          e.currentTarget.style.color = "#c39d63";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderLeft = "3px solid transparent";
                          e.currentTarget.style.color = "#ffffffe6";
                        }}
                      >
                        {subItem.label}
                      </div>
                    ))}
                </div>
              </div>

              {/* Footer */}
              <div style={textContainerStyle}>
                <div style={{ color: "#77A04A" }}>Bio</div>
                <div style={glutenFreeStyle}>Glutenfrei</div>
              </div>
            </Container>
          </Offcanvas.Body>
        </Offcanvas>
      </Container>
    </Navbar>
  );
};

export default Navigation;
